import { computed, Ref } from 'vue'
import { useMutation } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { useApiClient } from '@/services/api-client'
import { setAuthToken } from '@/services/auth-token'

interface SignInPayload {
  email: string
  password: string
}

interface SignInResponseType {
  token: string
}

interface ProblemDetailsType {
  type: string
  status: number
  title: string
  detail: string
}

interface UseSignInMutationResponse {
  isPending: Ref<boolean>
  isSuccess: Ref<boolean>
  isError: Ref<boolean>
  errorMessage: Ref<string | undefined>
  signIn: (payload: SignInPayload) => void
}

export const useSignInMutation = (
  onSuccess: () => void
): UseSignInMutationResponse => {
  const mutation = useMutation({
    mutationFn: (payload: SignInPayload) =>
      useApiClient()
        .post<SignInResponseType>('auth/sign_in', payload)
        .then((response) => response.data),
    onSuccess: (data) => {
      setAuthToken(data.token)
      onSuccess()
    }
  })

  function signIn(payload: SignInPayload) {
    mutation.mutate(payload)
  }

  const errorMessage = computed(() => {
    const error = mutation.error.value as AxiosError<ProblemDetailsType> | null
    return error?.response?.data?.detail
  })

  return { ...mutation, errorMessage, signIn }
}

export async function requestSignOut(): Promise<void> {
  await useApiClient().delete('auth/sign_out')
}
